import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  AlertCircle, CheckCircle2, Circle, Clock, Loader2, RefreshCw, Send, Smartphone,
} from "lucide-react";
import { fetchSmsSenders, registerSmsSender } from "../../features/communications/api/communicationsApi";

/**
 * SMS Senders (Settings, super admin only). Monday 13377714139, Christian:
 * agents should text clients from their own mobile, like realestate.com.au.
 *
 * Mobile Message only sends from a mobile once it is an active sender on our
 * account. "Send verification link" texts the agent a link; the number goes
 * Active once they tap it. From then on the texts that agent sends to clients
 * go out from their number (Christian, 27 Oct 2026: "please turn it on for
 * us"); the backend decides that per text, this screen registers and lists.
 */

const TYPE_LABELS = {
  alpha: "Business name",
  own: "Agent mobile",
  dedicated: "Dedicated number",
  shared: "Shared number",
};

const STATUS = {
  active: { label: "Active", Icon: CheckCircle2, className: "bg-emerald-50 text-emerald-700" },
  pending: { label: "Waiting for agent to tap link", Icon: Clock, className: "bg-amber-50 text-amber-800" },
  unknown: { label: "Couldn't check", Icon: AlertCircle, className: "bg-gray-100 text-gray-600" },
  none: { label: "Not registered", Icon: Circle, className: "bg-gray-100 text-gray-600" },
};

const StatusPill = ({ status }) => {
  const { label, Icon, className } = STATUS[status] || STATUS.none;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${className}`}>
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      {label}
    </span>
  );
};

const SmsSendersSettings = () => {
  const [data, setData] = useState({ senders: [], agents: [], error: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [agentId, setAgentId] = useState("");
  const [number, setNumber] = useState("");
  const [result, setResult] = useState(null); // { ok, text }
  const formRef = useRef(null);
  const numberRef = useRef(null);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      setData(await fetchSmsSenders());
    } catch (err) {
      setData((d) => ({
        ...d,
        error: err?.response?.data?.detail || "Couldn't load the sender list. Please try again.",
      }));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pickAgent = (id) => {
    setAgentId(id);
    setResult(null);
    const agent = data.agents.find((a) => String(a.id) === String(id));
    setNumber(agent ? agent.registered_number || agent.mobile || "" : "");
  };

  // "Register" / "Resend link" in the table fills the form and jumps to it.
  const startFor = (agent) => {
    pickAgent(String(agent.id));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => numberRef.current?.focus(), 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agentId) {
      setResult({ ok: false, text: "Pick the agent first." });
      return;
    }
    if (!number.trim()) {
      setResult({ ok: false, text: "Enter the agent's mobile number." });
      numberRef.current?.focus();
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await registerSmsSender(Number(agentId), number.trim());
      setData(res);
      setResult({ ok: true, text: res.detail });
      toast.success("Verification link sent.");
    } catch (err) {
      setResult({
        ok: false,
        text: err?.response?.data?.detail || "Couldn't reach Mobile Message. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent";
  const primaryBtn =
    "inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#55C5D0] text-white rounded-lg text-sm font-medium hover:bg-[#3bb3be] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#55C5D0] disabled:opacity-50 whitespace-nowrap";
  const linkBtn =
    "text-[#2a9aa5] text-sm font-medium hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55C5D0] rounded";
  const th = "px-3 py-2 text-left text-xs font-medium text-gray-500";
  const td = "px-3 py-3 text-sm text-gray-700 align-middle";

  return (
    <section id="sms-senders" className="bg-white rounded-2xl shadow border border-gray-100 p-6">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center">
          <Smartphone className="w-5 h-5 text-[#55C5D0] mr-2" aria-hidden="true" />
          <h3 className="text-lg font-semibold text-gray-800">SMS Senders</h3>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={loading || refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55C5D0] disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
          Refresh
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-1 max-w-[70ch]">
        Register an agent's own mobile with Mobile Message so texts can come from their number
        instead of COLLINGS. Mobile Message texts the agent a link, and the number is ready once
        they tap it.
      </p>
      <p className="text-xs text-gray-500 mb-5 max-w-[70ch]">
        Once an agent is Active, the texts they send to clients come from their own mobile and
        replies go to their phone. Everyone else still sends from COLLINGS. Only super admins can
        see this tab.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Loading…
        </div>
      ) : (
        <>
          {/* Register */}
          <form ref={formRef} onSubmit={handleSubmit} className="rounded-xl bg-gray-50 p-4 mb-6">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
              <div>
                <label htmlFor="sms-sender-agent" className="block text-xs font-medium text-gray-600 mb-1">
                  Agent
                </label>
                <select
                  id="sms-sender-agent"
                  value={agentId}
                  onChange={(e) => pickAgent(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Choose an agent…</option>
                  {data.agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name || a.email}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="sms-sender-number" className="block text-xs font-medium text-gray-600 mb-1">
                  Their mobile
                </label>
                <input
                  id="sms-sender-number"
                  ref={numberRef}
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  placeholder="0412 345 678"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  className={`${inputClass} tabular-nums`}
                />
              </div>
              <button type="submit" disabled={busy} className={primaryBtn}>
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="w-4 h-4" aria-hidden="true" />
                )}
                Send verification link
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              The link goes to the agent's phone. Ask them to tap it, then press Refresh.
            </p>
            {result && (
              <p
                role="status"
                className={`mt-3 flex items-start gap-1.5 text-sm ${result.ok ? "text-emerald-700" : "text-red-600"}`}
              >
                {result.ok ? (
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                ) : (
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                )}
                <span>{result.text}</span>
              </p>
            )}
          </form>

          {data.error && (
            <div className="flex items-start gap-2 rounded-xl bg-amber-50 text-amber-900 px-4 py-3 mb-6 text-sm" role="alert">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{data.error}</span>
            </div>
          )}

          {/* Agents. The table boxes are `relative` so the sr-only "Action"
              header stays inside them; unanchored it escapes Settings'
              overflow clip and scrolls the whole page sideways on a phone. */}
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Agents</h4>
          <div className="relative overflow-x-auto border border-gray-100 rounded-xl mb-8">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={th}>Agent</th>
                  <th scope="col" className={th}>Mobile</th>
                  <th scope="col" className={th}>Status</th>
                  <th scope="col" className={th}><span className="sr-only">Action</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.agents.length === 0 ? (
                  <tr>
                    <td colSpan={4} className={`${td} text-gray-500`}>No agents found.</td>
                  </tr>
                ) : (
                  data.agents.map((a) => (
                    <tr key={a.id}>
                      <td className={`${td} font-medium text-gray-800`}>{a.name || a.email}</td>
                      <td className={`${td} tabular-nums whitespace-nowrap`}>
                        {a.mobile || <span className="text-gray-400">No mobile on file</span>}
                      </td>
                      <td className={td}><StatusPill status={a.status} /></td>
                      <td className={`${td} text-right whitespace-nowrap`}>
                        {a.status !== "active" && (
                          <button type="button" onClick={() => startFor(a)} className={linkBtn}>
                            {a.status === "pending" ? "Resend link" : "Register"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Active senders on the account */}
          <h4 className="text-sm font-semibold text-gray-800 mb-1">Active senders on Mobile Message</h4>
          <p className="text-xs text-gray-500 mb-2">
            Everything our Mobile Message account can send from right now.
          </p>
          <div className="relative overflow-x-auto border border-gray-100 rounded-xl">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={th}>Sender</th>
                  <th scope="col" className={th}>Type</th>
                  <th scope="col" className={th}>Agent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.senders.length === 0 ? (
                  <tr>
                    <td colSpan={3} className={`${td} text-gray-500`}>
                      {data.error ? "Couldn't read the list." : "No senders yet."}
                    </td>
                  </tr>
                ) : (
                  data.senders.map((s) => (
                    <tr key={s.sender}>
                      <td className={`${td} font-medium text-gray-800 tabular-nums whitespace-nowrap`}>
                        {s.display}
                        {s.is_default && (
                          <span className="ml-2 rounded-full bg-[#55C5D0]/10 px-2 py-0.5 text-xs font-medium text-[#2a9aa5]">
                            Default
                          </span>
                        )}
                      </td>
                      <td className={td}>{TYPE_LABELS[s.type] || s.type}</td>
                      <td className={td}>
                        {s.agent ? s.agent.name : (
                          <span className="text-gray-400">{s.label || "Not linked to an agent"}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

export default SmsSendersSettings;
